/**
 * Sanitize a possibly-incomplete markdown string for display during the
 * IN-PROGRESS (still streaming) state of an assistant message.
 *
 * While the model types a message character-by-character, the raw markdown
 * syntax of a token that has not finished generating is briefly visible — you
 * literally see `[label](http...` before it snaps into a rendered link. This
 * helper hides that raw syntax for the trailing, still-incomplete token so the
 * stream only ever reveals plain label text (or nothing) until the token is
 * fully formed, at which point the untouched markdown renders normally.
 *
 * It deliberately only neutralizes the trailing/incomplete region. Any
 * fully-formed markdown earlier in the string passes through unchanged, so the
 * caller MUST stop applying this once the message is finalized and render the
 * complete markdown verbatim.
 *
 * Scope is intentionally narrow (it is NOT a markdown parser): incomplete
 * inline links and images, plus a dangling inline-code or emphasis marker at
 * the very tail.
 */
export function sanitizeStreamingMarkdown(partial: string): string {
  if (!partial) return partial;

  let out = partial;
  out = stripIncompleteLink(out);
  out = neutralizeTrailingInlineCode(out);
  out = neutralizeTrailingEmphasis(out);
  return out;
}

/**
 * Replace a trailing, not-yet-closed inline link or image with just its label
 * text. Handles, anchored at the end of the string so earlier complete links
 * are never touched:
 *   - `[label](partialUrl`  (open paren, no closing paren yet)
 *   - `[label]`             (closing bracket, no paren yet)
 *   - `[partial label`      (no closing bracket yet)
 * A leading `!` (image) is consumed as well so an in-progress image shows its
 * alt text rather than a stray `!`.
 *
 * The `[^[\]\n]` character class keeps each match within a single bracket pair
 * on a single line, so a completed link like `[a](b)` (which ends in `)`)
 * cannot match any of these patterns.
 */
function stripIncompleteLink(s: string): string {
  // `[label](partialUrl` — open paren, no closing paren yet.
  const openUrl = /!?\[([^[\]\n]*)\]\([^()\n]*$/;
  if (openUrl.test(s)) return s.replace(openUrl, "$1");

  // `[label]` — closing bracket but no `(` started yet.
  const closedLabel = /!?\[([^[\]\n]*)\]$/;
  if (closedLabel.test(s)) return s.replace(closedLabel, "$1");

  // `[partial label` — no closing bracket yet.
  const openLabel = /!?\[([^[\]\n]*)$/;
  if (openLabel.test(s)) return s.replace(openLabel, "$1");

  return s;
}

/**
 * Remove a single unmatched inline-code backtick so a half-typed `` `code ``
 * span shows the code text plainly instead of a stray leading backtick. Fenced
 * code blocks (runs of backticks) are left alone — we only strip a lone
 * backtick that is not adjacent to another backtick.
 */
function neutralizeTrailingInlineCode(s: string): string {
  const backticks = (s.match(/`/g) ?? []).length;
  if (backticks % 2 === 0) return s;

  const idx = s.lastIndexOf("`");
  if (s[idx - 1] === "`" || s[idx + 1] === "`") return s;
  return s.slice(0, idx) + s.slice(idx + 1);
}

/**
 * Strip a dangling emphasis marker (`**`, `__`, `*`, `_`) when its count is odd
 * (an opener with no matching closer), so a half-typed `**bold` shows `bold`
 * rather than leading asterisks. Double markers are handled before single ones
 * so the two `*` of a `**` pair are not mistaken for unmatched single markers.
 */
function neutralizeTrailingEmphasis(s: string): string {
  let out = s;
  for (const marker of ["**", "__", "*", "_"]) {
    const count = out.split(marker).length - 1;
    if (count % 2 === 1) {
      const idx = out.lastIndexOf(marker);
      out = out.slice(0, idx) + out.slice(idx + marker.length);
    }
  }
  return out;
}
